import { useState, useEffect, useMemo } from 'react';
import {
  MdPerson,
  MdEdit,
  MdPeople,
  MdWork,
  MdAttachMoney,
  MdCheckCircle,
  MdStar,
  MdMessage,
  MdCalendarToday,
  MdPayment,
  MdCardMembership,
  MdDeleteForever,
  MdWarning,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { getUser, getAuthToken, saveAuth, logout } from '../../services/auth';
import api from '../../services/api';
import { t } from '../../utils/i18n';
import UserAccountSummary from '../../components/UserAccountSummary';
import ConfirmDialog from '../../components/ConfirmDialog';

export default function ProfilePage() {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    totalJobs: 0,
    earnings: 0,
    completed: 0,
    rating: 0
  });
  const [jobs, setJobs] = useState([]);
  const [friends, setFriends] = useState([]);
  const [unpaidDebt, setUnpaidDebt] = useState(null);
  const [manualPaymentDates, setManualPaymentDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelSubOpen, setCancelSubOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [deleteEmailInput, setDeleteEmailInput] = useState('');
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');
  const navigate = useNavigate();

  const isParent = user?.role === 'parent';
  const isPlatformAdmin = user?.role === 'platform_admin';

  const premiumUntilRaw = user?.premiumUntil || user?.premium_until;
  const premiumUntilFormatted = useMemo(() => {
    if (!premiumUntilRaw) return null;
    const d = new Date(premiumUntilRaw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('sr-RS', { day: 'numeric', month: 'long', year: 'numeric' });
  }, [premiumUntilRaw]);
  const activeBabysitters = useMemo(() => (
    friends.filter(f => f.relationshipType === 'babysitter' && f.hasAccount).length
  ), [friends]);

  const userJobs = useMemo(() => {
    if (!user?.id) return [];
    return jobs.filter(job =>
      job.parentId === user.id ||
      job.babysitterId === user.id ||
      job.serviceUserId === user.id ||
      job.serviceProviderId === user.id
    );
  }, [jobs, user]);

  const activeJobsCount = useMemo(() => (
    userJobs.filter(j => j.status === 'accepted' || j.status === 'in_progress').length
  ), [userJobs]);

  const nextManualPayment = useMemo(() => {
    const now = new Date();
    return (manualPaymentDates || [])
      .map((entry) => {
        if (!entry?.date) return null;
        const [y, m, d] = entry.date.split('-').map(Number);
        const [hh, mm] = (entry.time || '14:00').split(':').map(Number);
        const dateObj = new Date(y, m - 1, d, hh || 14, mm || 0, 0, 0);
        return Number.isNaN(dateObj.getTime()) ? null : dateObj;
      })
      .filter(Boolean)
      .filter((dateObj) => dateObj >= now)
      .sort((a, b) => a - b)[0] || null;
  }, [manualPaymentDates]);

  const quickActions = useMemo(() => {
    if (isParent) {
      return [
        { label: t('profile.friends') || 'Prijatelji', icon: MdPeople, onClick: () => navigate('/dashboard/friends') },
        { label: t('profile.payments') || 'Plaćanja', icon: MdPayment, onClick: () => navigate('/dashboard/payments') },
        { label: t('profile.listings') || 'Oglasi', icon: MdWork, onClick: () => navigate('/dashboard/jobs') },
        { label: t('profile.messages') || 'Poruke', icon: MdMessage, onClick: () => navigate('/dashboard/conversations') },
      ];
    }
    return [
      { label: t('profile.listings') || 'Oglasi', icon: MdWork, onClick: () => navigate('/dashboard/jobs') },
      { label: t('profile.editProfile') || 'Izmeni profil', icon: MdEdit, onClick: () => navigate('/dashboard/profile/edit') },
      { label: t('profile.messages') || 'Poruke', icon: MdMessage, onClick: () => navigate('/dashboard/conversations') },
    ];
  }, [isParent, navigate]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = getAuthToken();
      const meRes = token ? await api.get('/auth/me').catch(() => null) : null;
      const currentUser = meRes?.data?.user ? meRes.data.user : getUser();
      if (meRes?.data?.user && token) {
        saveAuth(token, meRes.data.user);
      }
      setUser(currentUser);

      const [
        jobsResponse,
        friendsResponse,
        unpaidDebtResponse,
        manualDatesResponse
      ] = await Promise.all([
        api.get('/jobs').catch(() => ({ data: { jobs: [] } })),
        api.get('/friends').catch(() => ({ data: [] })),
        api.get('/payments/unpaid-debt').catch(() => ({ data: null })),
        api.get('/calendar-entries/manual-payment-dates').catch(() => ({ data: { manualPaymentDates: [] } }))
      ]);

      const jobs = jobsResponse.data?.jobs || (Array.isArray(jobsResponse.data) ? jobsResponse.data : []);
      setJobs(jobs);
      const friendsData = Array.isArray(friendsResponse.data) ? friendsResponse.data : [];
      setFriends(friendsData);
      setUnpaidDebt(unpaidDebtResponse?.data || null);

      const manualDatesRaw = manualDatesResponse?.data?.manualPaymentDateTimes
        || manualDatesResponse?.data?.manualPaymentDates
        || [];
      const normalizedManualDates = (manualDatesRaw || []).map((item) => {
        if (!item) return null;
        if (typeof item === 'string') {
          return { date: item.split('T')[0], time: '14:00' };
        }
        const rawDate = item.date || item.paymentDate || item.payment_date;
        if (!rawDate) return null;
        const normalizedDate = typeof rawDate === 'string'
          ? rawDate.split('T')[0]
          : rawDate instanceof Date
            ? rawDate.toISOString().split('T')[0]
            : null;
        if (!normalizedDate) return null;
        const rawTime = item.time || item.paymentTime || item.payment_time || '14:00';
        const cleanedTime = typeof rawTime === 'string' && /^\d{2}:\d{2}/.test(rawTime)
          ? rawTime.slice(0, 5)
          : '14:00';
        return { date: normalizedDate, time: cleanedTime };
      }).filter(Boolean);
      setManualPaymentDates(normalizedManualDates);
      
      const userJobs = jobs.filter(job => 
        job.parentId === currentUser.id || 
        job.babysitterId === currentUser.id ||
        job.serviceUserId === currentUser.id ||
        job.serviceProviderId === currentUser.id
      );

      setStats({
        totalJobs: userJobs.length,
        earnings: userJobs
          .filter(j => j.paymentStatus === 'paid')
          .reduce((sum, j) => sum + (j.totalAmount || 0), 0),
        completed: userJobs.filter(j => j.status === 'completed').length,
        rating: currentUser?.rating?.average || 0
      });
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden relative">
      <div className="relative z-10 p-4 lg:p-6 space-y-6 max-w-7xl mx-auto w-full">
        {/* Profile Header */}
        <div className="hidden lg:block">
          <div className="surface-panel flex items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-primary-600/10 flex items-center justify-center">
                <MdPerson className="text-primary-600 dark:text-primary-400 text-2xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('profile.title') || 'Profil'}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('profile.subtitle') || 'Pregled naloga, statistika i brze akcije.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/dashboard/profile/edit')}
              className="btn-primary text-sm px-4 py-2"
            >
              {t('profile.edit') || 'Izmeni profil'}
            </button>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-4 lg:gap-6">
            <div className="w-16 h-16 lg:w-24 lg:h-24 rounded-2xl bg-primary-600 flex items-center justify-center text-white text-2xl lg:text-3xl font-bold flex-shrink-0 overflow-hidden ring-4 ring-primary-100 dark:ring-primary-900/40">
              {user?.photoUrl || user?.profileImage ? (
                <img src={user.photoUrl || user.profileImage} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                user?.firstName?.[0]?.toUpperCase() || 'U'
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate flex items-center gap-2">
                {user?.firstName} {user?.lastName}
                {user?.role && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-semibold">
                    {user.role === 'parent' ? (t('profile.parent') || 'Roditelj') : user.role === 'babysitter' ? (t('profile.babysitter') || 'Bebisiterka') : user.role}
                  </span>
                )}
              </h2>
              <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1 truncate">
                {user?.email}
              </p>
              {user?.phone && (
                <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400 truncate">
                  {user.phone}
                </p>
              )}
              <button
                onClick={() => navigate('/dashboard/profile/edit')}
                className="mt-4 btn-secondary flex items-center gap-2 w-fit text-sm lg:text-base lg:hidden"
              >
                <MdEdit />
                {t('profile.edit') || 'Edit Profile'}
              </button>
            </div>
          </div>
        </div>

        <UserAccountSummary user={user} />

        {/* Premium / Pretplata */}
        <div className="card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
              <MdCardMembership className="text-primary-600 dark:text-primary-400 text-xl" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {user?.hasPremium ? (t('profile.premiumActive') || 'Premium aktiviran') : (t('profile.premiumInactive') || 'Pretplata za dopisivanje')}
              </h3>
              {user?.hasPremium && premiumUntilFormatted && (
                <p className="text-sm font-medium text-primary-700 dark:text-primary-300 mt-1">
                  {t('profile.premiumValidUntil') || 'Pretplata važi do'}: {premiumUntilFormatted}
                </p>
              )}
              {user?.hasPremium && !premiumUntilFormatted && (
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {t('profile.premiumNoEndDate') || 'Datum isteka nije dostupan.'}
                </p>
              )}
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {user?.hasPremium
                  ? (t('profile.premiumActiveDesc') || 'Možete neograničeno da šaljete poruke i kontaktirate korisnike.')
                  : (t('profile.premiumInactiveDesc') || 'Aktivirajte pretplatu da biste mogli da šaljete poruke i dopisujete se sa dadiljama ili roditeljima.')}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
            {user?.hasPremium ? (
              <>
                <span className="inline-flex items-center justify-center sm:justify-end gap-1.5 px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm font-medium">
                  <MdCheckCircle className="text-lg" />
                  {t('profile.active') || 'Aktivan'}
                </span>
                <button
                  type="button"
                  onClick={() => setCancelSubOpen(true)}
                  className="text-sm font-semibold px-4 py-2 rounded-xl border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  {t('profile.cancelSubscription') || 'Otkaži pretplatu'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/dashboard?tab=pricing')}
                className="btn-primary text-sm px-4 py-2 whitespace-nowrap w-full sm:w-auto"
              >
                {t('profile.activatePremium') || 'Aktiviraj Premium'}
              </button>
            )}
          </div>
        </div>

        {/* Opasna zona — brisanje naloga */}
        {!isPlatformAdmin && (
          <div className="rounded-2xl border-2 border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20 p-4 lg:p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0">
                <MdWarning className="text-red-600 dark:text-red-400 text-xl" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-red-900 dark:text-red-200">
                  {t('profile.dangerZone') || 'Opasna zona'}
                </h3>
                <p className="text-sm text-red-800/90 dark:text-red-300/90 mt-1">
                  {t('profile.deleteAccountLead') ||
                    'Ova akcija je nepovratna. Svi vaši podaci, poruke, kalendar, prijatelji i istorija biće trajno uklonjeni.'}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteEmailInput('');
                    setDeleteAccountError('');
                    setDeleteAccountOpen(true);
                  }}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors"
                >
                  <MdDeleteForever className="text-lg" />
                  {t('profile.deleteAccountTitle') || 'Trajno obriši nalog'}
                </button>
              </div>
            </div>
          </div>
        )}

        {isPlatformAdmin && (
          <p className="text-sm text-gray-500 dark:text-gray-400 px-1">
            {t('profile.deleteAccountAdminBlocked') || 'Administratorski nalog se ne može obrisati iz aplikacije.'}
          </p>
        )}

        <ConfirmDialog
          isOpen={cancelSubOpen}
          onClose={() => setCancelSubOpen(false)}
          onConfirm={async () => {
            const token = getAuthToken();
            try {
              const res = await api.post('/auth/cancel-premium');
              const u = res.data?.user;
              if (u && token) saveAuth(token, u);
              if (u) setUser(u);
            } catch (e) {
              console.error(e);
              alert(t('profile.cancelSubscriptionError') || 'Greška pri otkazivanju pretplate.');
              throw e;
            }
          }}
          title={t('profile.cancelSubscription') || 'Otkaži pretplatu'}
          message={t('profile.cancelSubscriptionConfirm') || 'Da li želite da otkažete pretplatu?'}
          confirmText={t('profile.cancelSubscription') || 'Otkaži pretplatu'}
          cancelText={t('common.close') || t('common.cancel') || 'Zatvori'}
          type="danger"
        />

        {deleteAccountOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60">
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 border border-red-200 dark:border-red-900/50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-account-title"
            >
              <div className="flex items-start gap-3 mb-4">
                <MdDeleteForever className="text-3xl text-red-600 flex-shrink-0" />
                <div>
                  <h3 id="delete-account-title" className="text-lg font-bold text-red-700 dark:text-red-300">
                    {t('profile.deleteAccountTitle') || 'Trajno obriši nalog'}
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                    {t('profile.deleteAccountWarning') ||
                      'Nećete moći da povratite nalog. Unesite tačan email naloga.'}
                  </p>
                </div>
              </div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                {t('profile.deleteAccountEmailLabel') || 'Email naloga'}
              </label>
              <input
                type="email"
                autoComplete="off"
                value={deleteEmailInput}
                onChange={(e) => {
                  setDeleteEmailInput(e.target.value);
                  setDeleteAccountError('');
                }}
                placeholder={user?.email || (t('profile.deleteAccountEmailPlaceholder') || 'vas@email.com')}
                className="w-full input-base text-sm mb-2"
              />
              {deleteAccountError && (
                <p className="text-sm text-red-600 dark:text-red-400 mb-3">{deleteAccountError}</p>
              )}
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-4">
                <button
                  type="button"
                  className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-medium"
                  disabled={deleteAccountLoading}
                  onClick={() => !deleteAccountLoading && setDeleteAccountOpen(false)}
                >
                  {t('common.cancel') || 'Otkaži'}
                </button>
                <button
                  type="button"
                  disabled={
                    deleteAccountLoading ||
                    !user?.email ||
                    deleteEmailInput.trim().toLowerCase() !== String(user.email).trim().toLowerCase()
                  }
                  onClick={async () => {
                    if (!user?.email) return;
                    if (deleteEmailInput.trim().toLowerCase() !== String(user.email).trim().toLowerCase()) {
                      setDeleteAccountError(t('profile.deleteAccountMismatch') || 'Email se ne poklapa.');
                      return;
                    }
                    setDeleteAccountLoading(true);
                    setDeleteAccountError('');
                    try {
                      await api.post('/auth/delete-my-account', {
                        confirmEmail: deleteEmailInput.trim(),
                      });
                      setDeleteAccountOpen(false);
                      logout();
                      navigate('/login', { replace: true, state: { accountDeleted: true } });
                    } catch (err) {
                      const msg =
                        err?.response?.data?.message ||
                        (t('profile.deleteAccountError') || 'Brisanje naloga nije uspelo.');
                      setDeleteAccountError(msg);
                    } finally {
                      setDeleteAccountLoading(false);
                    }
                  }}
                  className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteAccountLoading ? '…' : (t('profile.deleteAccountConfirmButton') || 'Obriši nalog zauvek')}
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Highlights */}
      {isParent ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdPeople className="text-2xl text-primary-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.activeBabysitters') || 'Aktivne dadilje'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-primary-600">
              {activeBabysitters}
            </p>
          </div>
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdPayment className="text-2xl text-green-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.currentDebt') || 'Trenutno dugovanje'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-green-600">
              {unpaidDebt?.totalDebt ? `${unpaidDebt.totalDebt.toFixed(0)} RSD` : '0 RSD'}
            </p>
          </div>
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdCalendarToday className="text-2xl text-amber-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.nextPayment') || 'Sledeće plaćanje'}
              </h3>
            </div>
            <p className="text-lg font-semibold text-amber-700 dark:text-amber-300">
              {nextManualPayment
                ? nextManualPayment.toLocaleDateString('sr-RS', { day: 'numeric', month: 'long', year: 'numeric' })
                : (t('profile.notSet') || 'Nije podešeno')}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdWork className="text-2xl text-primary-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.activeJobs') || 'Aktivni poslovi'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-primary-600">
              {activeJobsCount}
            </p>
          </div>
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdCheckCircle className="text-2xl text-indigo-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.completed') || 'Završeno'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-indigo-600">
              {stats.completed}
            </p>
          </div>
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdAttachMoney className="text-2xl text-green-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.earnings') || 'Zarada'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-green-600">
              {stats.earnings.toFixed(0)} RSD
            </p>
          </div>
          <div className="card">
            <div className="flex items-center gap-3 mb-2">
              <MdStar className="text-2xl text-yellow-600" />
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('profile.rating') || 'Ocena'}
              </h3>
            </div>
            <p className="text-3xl font-bold text-yellow-600">
              {stats.rating > 0 ? stats.rating.toFixed(1) : 'N/A'}
            </p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t('profile.quickActions') || 'Brze akcije'}
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                key={`${action.label}-${index}`}
                onClick={action.onClick}
                className="card text-left hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-primary-600/10 flex items-center justify-center">
                    <Icon className="text-xl text-primary-600" />
                  </div>
                  <span className="text-gray-400">→</span>
                </div>
                <div className="mt-3">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {action.label}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('profile.openSection') || 'Otvori sekciju'}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* User Information */}
      {user && (
        <div>
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
            {t('profile.information') || 'Informacije'}
          </h2>
          <div className="card">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
                <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {t('profile.role') || 'Uloga'}
                </label>
                <p className="text-gray-900 dark:text-gray-100 font-semibold capitalize mt-2">
                  {user.role === 'parent' ? (t('profile.parent') || 'Roditelj') : user.role === 'babysitter' ? (t('profile.babysitter') || 'Bebisiterka') : user.role}
                </p>
              </div>

              {user.hourlyRate && (
                <div className="rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
                  <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('profile.hourlyRate') || 'Satnica'}
                  </label>
                  <p className="text-gray-900 dark:text-gray-100 font-semibold mt-2">
                    {user.hourlyRate} RSD/h
                  </p>
                </div>
              )}

              {user.phone && (
                <div className="rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
                  <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('profile.phone') || 'Telefon'}
                  </label>
                  <p className="text-gray-900 dark:text-gray-100 mt-2">
                    {user.phone}
                  </p>
                </div>
              )}

              {user.address && typeof user.address === 'object' && (
                <div className="rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
                  <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('profile.address') || 'Adresa'}
                  </label>
                  <p className="text-gray-900 dark:text-gray-100 mt-2">
                    {[user.address.street, user.address.city, user.address.postalCode]
                      .filter((s) => s != null && typeof s === 'string')
                      .join(', ') || '—'}
                  </p>
                </div>
              )}
            </div>

            {user.cities && user.cities.length > 0 && (
              <div className="mt-4 rounded-xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-900/60 p-4">
                <label className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {t('profile.cities') || 'Gradovi'}
                </label>
                <p className="text-gray-900 dark:text-gray-100 mt-2">
                  {user.cities.join(', ')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

